import React, { useState, useEffect, useRef } from 'react';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import { testimonials } from '../data/mock';

export const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const autoPlayRef = useRef(null);

  // Auto-play functionality
  useEffect(() => {
    if (!isHovered) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }, 6000); // 6 seconds for comfortable reading
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isHovered]);

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Calculate visible testimonials (show 3 at a time with center one highlighted)
  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + testimonials.length) % testimonials.length;
      visible.push({
        ...testimonials[index],
        position: i,
        isCenter: i === 0
      });
    }
    return visible;
  };

  const visibleTestimonials = getVisibleTestimonials();

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
            Trusted by entrepreneurs and businesses nationwide
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Don't just take our word for it. Hear from the innovators, creators, and business owners we've helped protect.
          </p>
        </div>

        {/* Navigation Arrows - Above Testimonials */}
        <div className="flex items-center justify-end mb-6">
          <div className="flex items-center space-x-2">
            <button
              onClick={goToPrevious}
              className="w-12 h-12 bg-white hover:bg-[#D4AF37] rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg group"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="w-6 h-6 text-[#0A1F44] group-hover:text-white" />
            </button>
            <button
              onClick={goToNext}
              className="w-12 h-12 bg-white hover:bg-[#D4AF37] rounded-full flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg group"
              aria-label="Next testimonial"
            >
              <ChevronRight className="w-6 h-6 text-[#0A1F44] group-hover:text-white" />
            </button>
          </div>
        </div>

        {/* Testimonials Carousel */}
        <div 
          className="relative overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex items-center justify-center gap-6 py-4">
            {visibleTestimonials.map((testimonial, index) => {
              const isCenter = testimonial.isCenter;
              
              return (
                <div
                  key={`${testimonial.id}-${index}`}
                  className={`transition-all duration-500 ${
                    isCenter
                      ? 'w-full max-w-2xl scale-100 opacity-100'
                      : 'w-full max-w-xl scale-90 opacity-40 pointer-events-none'
                  }`}
                >
                  <div
                    className={`bg-white rounded-2xl p-8 relative transition-all duration-500 ${
                      isCenter
                        ? 'shadow-2xl border-2 border-[#D4AF37]'
                        : 'shadow-md border-2 border-transparent'
                    }`}
                  >
                    {/* Quote Icon */}
                    <div className="absolute top-6 right-6 opacity-10">
                      <Quote className="w-12 h-12 text-[#D4AF37]" />
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <p className={`text-gray-700 leading-relaxed mb-6 transition-all ${
                        isCenter ? 'text-lg' : 'text-base'
                      }`}>
                        "{testimonial.content}"
                      </p>

                      {/* Author Info */}
                      <div className="flex items-center space-x-4 pt-4 border-t border-gray-100">
                        <img
                          src={testimonial.image}
                          alt={testimonial.name}
                          className="w-14 h-14 rounded-full object-cover border-2 border-[#D4AF37]"
                        />
                        <div>
                          <div className="font-bold text-[#0A1F44]">{testimonial.name}</div>
                          <div className="text-gray-600 text-sm">{testimonial.role}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress Dots */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex
                    ? 'w-8 h-3 bg-[#D4AF37]'
                    : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 text-lg">
            Join hundreds of satisfied clients who trust us with their intellectual property.
          </p>
        </div>
      </div>
    </section>
  );
};